import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    useTheme,
    Stack,
    Alert,
    Typography
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import InputField from '../../components/formItem/inputField';
import BoxData from '../../components/boxData';
import Toggle from '../../components/toggle';
import { HeaderCell } from '../../components/table/headerCell';
import { Search, Prev, Next } from '../../assets/images';
import { getLabelByValue, formatBirthDate, formatDateTimeDisplay, isAdmin } from '../../utils/base';
import { ORGANIZATION_TYPES, USER_GENDERS, API_PAGE_SIZE } from '../../constants';
import { userAPI, rankAPI, membershipAPI } from '../../actions/imsServer';
import StatusIndicator from '../../components/statusIndicator';

const UserList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterValue, setFilterValue] = useState('all');
    const [rankingType, setRankingType] = useState(0); // 0: cumulative, 1: monthly
    const [todayUsageData, setTodayUsageData] = useState({}); // userId별 금일 사용량
    const [organizationData, setOrganizationData] = useState({}); // userId별 기관 데이터
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = API_PAGE_SIZE;
    const [organizationId, setOrganizationId] = useState(0);

    // meInfo에서 등급 확인
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // 검색 필드 상태
    const [searchFields, setSearchFields] = useState({
        userName: '',
        organizationName: ''
    });

    useEffect(() => {
        if (isAdmin(meInfo?.grade) && meInfo?.id) {
            const fetchOrganizationId = async () => {
                try {
                    // grade 50, 60 모두 membership에서 가져오기
                    const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                    const memberships = membershipsResponse?.data?.memberships || [];
                    if (memberships.length > 0) {
                        setOrganizationId(memberships[0].organizationId);
                    }
                } catch (err) {
                    console.error('Failed to fetch user organizations:', err);
                }
            }
            fetchOrganizationId();
        }
    }, [meInfo?.grade, meInfo?.id]);

    // 사용자 데이터에서 expand로 받아온 memberships 정보를 organizationData 형태로 변환
    const processOrganizationData = useCallback(async (usersData) => {
        const orgDataByUser = {};

        // 먼저 expand로 받아온 데이터 처리
        usersData.forEach(user => {
            // user.memberships가 expand로 포함되어 있는지 확인
            if (Array.isArray(user.memberships)) {
                user.memberships.forEach(membership => {
                    const userId = user.id;

                    if (userId && membership.organizationId) {
                        if (!orgDataByUser[userId]) {
                            orgDataByUser[userId] = [];
                        }

                        // expand로 받아온 membership 데이터를 최대한 활용
                        if ((membership.organizationId || membership.organizationName)) {
                            orgDataByUser[userId].push({
                                id: membership.organizationId,
                                name: membership.organizationName || '',
                                type: membership.type || '',
                                country: membership.country || '',
                                region: membership.region || '',
                                division: membership.division || (membership.divisionSections ? Object.keys(membership.divisionSections || {}).join(',') : ''),
                                section: membership.section || (membership.divisionSections ? Object.values(membership.divisionSections || {}).map(sections => sections.join(',')).join(',') : ''),
                                createDate: membership.createDate || '',
                                isMain: membership.info === 'main' || String(user.mainOrganizationId) === String(membership.organizationId)
                            });
                        }
                    }
                });
            }
        });

        setOrganizationData(orgDataByUser);
        return orgDataByUser;
    }, []);

    // 사용자 데이터 로드 함수 (공통 로직)
    const fetchUsersData = useCallback(async (searchParams = {}, page = 1) => {
        try {
            setError(null);

            // 초기 화면은 랭킹순으로 정렬
            // 랭킹 데이터를 먼저 가져와서 그 순서대로 사용자 데이터를 조회
            const offset = (page - 1) * ITEMS_PER_PAGE;
            let ranksResponse;

            // rankAPI 호출 파라미터 준비 (백엔드 기본 limit 100, offset만 전달)
            const rankParams = { offset };
            if (isAdmin(meInfo?.grade)) {
                rankParams.organizationId = organizationId || 0;
            }

            if (rankingType === 0) {
                // 누적랭킹
                ranksResponse = await rankAPI.getRanks(rankParams);
            } else {
                // 이달랭킹
                const now = new Date();
                const yyyy = String(now.getFullYear());
                const mm = String(now.getMonth() + 1).padStart(2, '0');
                const month = `${yyyy}-${mm}`;
                ranksResponse = await rankAPI.getMonthlyRanks({ ...rankParams, month });
            }

            const ranks = (ranksResponse?.data?.ranks || ranksResponse?.ranks || []);
            const total = ranksResponse?.data?.total || ranksResponse?.total || 0;

            // 랭킹 데이터에서 userId 목록 추출
            const rankedUserIds = ranks.map(r => r.userId).filter(id => id != null);

            // 랭킹 순서대로 사용자 데이터 가져오기
            let usersData = [];
            if (rankedUserIds.length > 0) {
                // users API는 userId를 복수개로 조회 가능 (userIdsParam 개수만큼 요청)
                const userIdsParam = rankedUserIds.join(',');
                const apiParams = {
                    state: 0,
                    grade: 1,
                    userId: userIdsParam,
                    expand: 'memberships,playInfo',
                    limit: rankedUserIds.length,
                    ...searchParams
                };

                const usersResponse = await userAPI.getUsers('all', apiParams);
                usersData = Array.isArray(usersResponse) ? usersResponse : (usersResponse?.data?.users || []);

                // 랭킹 순서대로 정렬 (rankedUserIds 순서 유지)
                const userMap = new Map(usersData.map(user => [user.id, user]));
                usersData = rankedUserIds
                    .map(userId => userMap.get(userId))
                    .filter(user => user != null);

                // 랭킹 정보 주입
                const rankMap = new Map(ranks.map(r => [r.userId, r.rank]));
                usersData = usersData.map(user => ({
                    ...user,
                    ...(rankingType === 0
                        ? { rankingCumulative: rankMap.get(user.id) ?? null }
                        : { rankingMonthly: rankMap.get(user.id) ?? null })
                }));
            }

            // playInfo.todayPlayTime에서 금일 사용량 추출하여 todayUsageData에 매핑
            const usageByUser = {};
            usersData.forEach(user => {
                if (user.id && user.playInfo?.todayPlayTime !== undefined) {
                    usageByUser[user.id] = user.playInfo.todayPlayTime;
                }
            });
            setTodayUsageData(usageByUser);

            setUsers(usersData);
            setTotalCount(total);

            // expand로 받아온 organization 정보 처리
            await processOrganizationData(usersData);

            return usersData;
        } catch (err) {
            console.error('사용자 데이터 로드 실패:', err);
            setError(t('failed_to_load_user_list'));
            setUsers([]);
            setTotalCount(0);
            return [];
        }
    }, [meInfo?.grade, rankingType, processOrganizationData, organizationId]);

    // 사용자 데이터 로드
    useEffect(() => {
        const fetchUsers = async () => {
            // 어드민인 경우 organizationId가 설정될 때까지 대기
            if (isAdmin(meInfo?.grade) && organizationId === 0) {
                return;
            }
            setLoading(true);
            setCurrentPage(1);
            setTotalCount(0);
            await fetchUsersData({}, 1);
            setLoading(false);
        };

        setUsers([]);
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchUsersData는 의존성에서 제외(반복 호출 방지)
    }, [organizationId, meInfo?.grade]);

    // 검색 파라미터 생성 함수
    const getSearchParams = useCallback(() => {
        let searchParams = {};

        if (filterValue === 'search_by_name') {
            if (searchFields.userName.trim()) {
                searchParams.keyword = searchFields.userName.trim();
            }
        }
        //  else if (filterValue === '기관명으로 검색') {
        //     if (searchFields.organizationName.trim()) {
        //         searchParams.keyword = searchFields.organizationName.trim();
        //     }
        // }

        return searchParams;
    }, [filterValue, searchFields]);

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;

        setCurrentPage(newPage);
        const searchParams = getSearchParams();
        fetchUsersData(searchParams, newPage);
    }, [totalCount, getSearchParams, fetchUsersData]);

    // 이전 페이지
    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    }, [currentPage, handlePageChange]);

    // 다음 페이지
    const handleNextPage = useCallback(() => {
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
    }, [currentPage, totalCount, handlePageChange]);

    // 검색 실행 함수
    const handleSearch = async () => {
        try {
            setLoading(true);
            setError(null);

            // 검색 파라미터 준비
            let searchParams = {};

            if (filterValue === 'search_by_name') {
                if (searchFields.userName.trim()) {
                    searchParams.keyword = searchFields.userName.trim();
                }
            }
            // else if (filterValue === '기관명으로 검색') {
            //     // 기관명 검색은 복잡한 로직이 필요하므로 별도 처리
            //     if (searchFields.organizationName.trim()) {
            //         // 기관명으로 기관 검색 (expand로 memberships 포함)
            //         const organizationsResponse = await organizationAPI.getOrganizations({
            //             state: 0,
            //             keyword: searchFields.organizationName.trim(),
            //             expand: 'memberships,didims,playInfo'
            //         });

            //         const organizations = organizationsResponse?.data?.organizations || organizationsResponse?.organizations || [];
            //         const organizationIds = organizations.map(org => org.id).filter(id => id != null);

            //         if (organizationIds.length === 0) {
            //             setUsers([]);
            //             setTotalCount(0);
            //             setCurrentPage(1);
            //             setLoading(false);
            //             return;
            //         }

            //         // organizationId 파라미터로 사용자 검색
            //         searchParams.organizationId = organizationIds.join(',');
            //     }
            // }

            // 데이터 초기화 후 검색
            setUsers([]);
            setCurrentPage(1);
            setTotalCount(0);
            await fetchUsersData(searchParams, 1);
        } catch (err) {
            console.error('검색 실패:', err);
            setError(t('an_error_occurred_while_searching'));
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (user) => {
        navigate('/users/detail', {
            state: {
                userData: user
            }
        });
    };

    const handleEditUser = (userId) => {
        // 사용자 수정 페이지로 이동
        navigate('/users/edit', {
            state: {
                userData: users.find(user => user.id === userId)
            }
        });
    };

    const handleAddUser = () => {
        navigate('/users/edit');
    };


    // 검색 필터 변경 시 전체 데이터로 리셋
    useEffect(() => {
        const resetData = async () => {
            try {
                setLoading(true);
                setError(null);

                // 어드민일 때 '기관명으로 검색'이 선택되어 있으면 '전체'로 변경
                if (isAdmin(meInfo?.grade) && filterValue === 'search_by_org_name') {
                    setFilterValue('all');
                    return;
                }

                // 초기 화면은 랭킹순으로 정렬 (100건씩, 백엔드 기본 limit)
                const offset = 0;
                let ranksResponse;

                const rankParams = { offset };
                if (isAdmin(meInfo?.grade)) {
                    rankParams.organizationId = organizationId || 0;
                }

                if (rankingType === 0) {
                    ranksResponse = await rankAPI.getRanks(rankParams);
                } else {
                    const now = new Date();
                    const yyyy = String(now.getFullYear());
                    const mm = String(now.getMonth() + 1).padStart(2, '0');
                    const month = `${yyyy}-${mm}`;
                    ranksResponse = await rankAPI.getMonthlyRanks({ ...rankParams, month });
                }

                const ranks = (ranksResponse?.data?.ranks || ranksResponse?.ranks || []);
                const total = ranksResponse?.data?.total || ranksResponse?.total || 0;

                // 랭킹 데이터에서 userId 목록 추출
                const rankedUserIds = ranks.map(r => r.userId).filter(id => id != null);

                // 랭킹 순서대로 사용자 데이터 가져오기
                let usersData = [];
                if (rankedUserIds.length > 0) {
                    // users API는 userId를 복수개로 조회 가능
                    const userIdsParam = rankedUserIds.join(',');
                    const apiParams = {
                        state: 0,
                        grade: 1,
                        userId: userIdsParam,
                        limit: rankedUserIds.length,
                        expand: 'memberships,playInfo'
                    };

                    const usersResponse = await userAPI.getUsers('all', apiParams);
                    usersData = Array.isArray(usersResponse) ? usersResponse : (usersResponse?.data?.users || []);

                    // 랭킹 순서대로 정렬 (rankedUserIds 순서 유지)
                    const userMap = new Map(usersData.map(user => [user.id, user]));
                    usersData = rankedUserIds
                        .map(userId => userMap.get(userId))
                        .filter(user => user != null);

                    // 랭킹 정보 주입
                    const rankMap = new Map(ranks.map(r => [r.userId, r.rank]));
                    usersData = usersData.map(user => ({
                        ...user,
                        ...(rankingType === 0
                            ? { rankingCumulative: rankMap.get(user.id) ?? null }
                            : { rankingMonthly: rankMap.get(user.id) ?? null })
                    }));
                }

                setUsers(usersData);
                setTotalCount(total);
                setCurrentPage(1);

                // expand로 받아온 organization 정보 처리
                await processOrganizationData(usersData);

                // playInfo.todayPlayTime에서 금일 사용량 추출하여 todayUsageData에 매핑
                const usageByUser = {};
                usersData.forEach(user => {
                    if (user.id && user.playInfo?.todayPlayTime !== undefined) {
                        usageByUser[user.id] = user.playInfo.todayPlayTime;
                    }
                });
                setTodayUsageData(usageByUser);
            } catch (err) {
                console.error('데이터 리셋 실패:', err);
                setError(t('failed_to_load_user_list'));
            } finally {
                setLoading(false);
            }
        };

        resetData();
        // 검색 필드 리셋
        setSearchFields({
            userName: '',
            organizationName: ''
        });
    }, [filterValue, meInfo?.grade, organizationId, rankingType, processOrganizationData]);

    // 검색 필드 변경 시 자동 필터링 제거 (엔터키 또는 돋보기 클릭 시에만 검색)

    // 랭킹 타입 변경 시 데이터 재조회 (랭킹순으로 정렬)
    useEffect(() => {
        const refreshData = async () => {
            const searchParams = getSearchParams();
            await fetchUsersData(searchParams, currentPage);
        };
        refreshData();
    }, [rankingType, getSearchParams, fetchUsersData, currentPage]);

    // 검색 필드 렌더링 함수
    const renderSearchFields = () => {
        switch (filterValue) {
            case 'search_by_name':
                return (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', alignSelf: 'stretch', gap: 2, flex: 0.5
                    }}>
                        <InputField
                            type="text"
                            placeholder={t('please_enter_your_name')}
                            value={searchFields.userName}
                            onChange={(e) => setSearchFields(prev => ({ ...prev, userName: e.target.value }))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                            onBlur={() => {
                                // 포커스 아웃 시 검색 실행
                                handleSearch();
                            }}
                            showClearIcon={true}
                            onClear={() => setSearchFields(prev => ({ ...prev, userName: '' }))}
                        />
                    </Box>
                );

            // case '기관명으로 검색':
            //     return (
            //         <Box sx={{
            //             display: 'flex', alignItems: 'center', alignSelf: 'stretch', gap: 2, flex: 0.5
            //         }}>
            //             <InputField
            //                 type="text"
            //                 placeholder="기관명을 입력해주세요"
            //                 value={searchFields.organizationName}
            //                 onChange={(e) => setSearchFields(prev => ({ ...prev, organizationName: e.target.value }))}
            //                 onKeyDown={(e) => {
            //                     if (e.key === 'Enter') {
            //                         e.preventDefault();
            //                         handleSearch();
            //                     }
            //                 }}
            //                 onBlur={() => {
            //                     // 포커스 아웃 시 검색 실행
            //                     handleSearch();
            //                 }}
            //                 showClearIcon={true}
            //                 onClear={() => setSearchFields(prev => ({ ...prev, organizationName: '' }))}
            //             />
            //         </Box>
            //     );

            default:
                return null;
        }
    };

    return (
        <ContentArea>
            {/* 검색 영역 */}
            <Box sx={{
                display: 'flex',
                width: '100%',
                px: 2.5,
                py: 2.5,
                mb: 3
            }}>
                {/* 검색 타입 선택 */}
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    flex: 1,
                    alignSelf: 'stretch'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Dropdown
                            options={[
                                { value: 'all', label: 'all' },
                                { value: 'search_by_name', label: 'search_by_name' },
                                //  ...(isAdmin(meInfo?.grade) ? [] : [{ value: 'search_by_org_name', label: 'search_by_org_name' }]),
                            ]}
                            value={filterValue}
                            onChange={(option) => setFilterValue(option.value)}
                            placeholder={t('select_search_type')}
                        />

                        {/* 검색 필드 */}
                        {renderSearchFields()}

                        <IconButton
                            onClick={handleSearch}
                            sx={{
                                display: filterValue === 'search_by_name' || filterValue === 'search_by_org_name' ? 'block' : 'none',
                                width: '24px',
                                height: '24px',
                                p: 0,
                                color: theme.palette.figma.iconWhite,
                                '&:hover': {
                                    backgroundColor: theme.palette.figma.white[20],
                                }
                            }}
                        >
                            <Search />
                        </IconButton>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* 랭킹 타입 토글 버튼 */}
                        <Toggle
                            options={[t('cumulative_ranking'), t('monthly_ranking')]}
                            value={rankingType}
                            onChange={setRankingType}
                            sx={{ width: 'auto', minWidth: '300px' }}
                        />
                    </Stack>
                </Box>
            </Box>

            {/* 에러 메시지 */}
            {error && (
                <Box sx={{ px: 2.5, mb: 2 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            )}

            {/* 테이블 */}
            <Box
                sx={{
                    px: 2.5,
                    mb: 3,
                }}
            >
                <TableContainer component={Paper}>
                    <Table aria-label="user table">
                        <TableHead>
                            <TableRow>
                                <HeaderCell
                                    label={t('ranking')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('name')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('gender')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('birth_date')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('exp')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('available_points')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('daily_didim_usage')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('watch_linkage')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('main_organization')}
                                    align="center"
                                />
                                <HeaderCell label={t('organization_name')} align="center" />
                                <HeaderCell label={t('organization_type')} align="center" />
                                <HeaderCell label={t('division_1')} align="center" />
                                <HeaderCell label={t('division_2')} align="center" />
                                <HeaderCell
                                    label={t('organization_join_date')}
                                    align="center"
                                />
                                <HeaderCell
                                    label={t('edit')}
                                    align="center"
                                />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.isArray(users) && users.length > 0 ? users.map((user, userIndex) => {
                                const userOrganizations = organizationData[user.id] || [];
                                const isEvenUser = userIndex % 2 === 0;
                                const backgroundColor = isEvenUser ? theme.palette.figma.white[5] : theme.palette.figma.white[20];

                                // 기관이 없으면 기본 행 하나 표시
                                if (userOrganizations.length === 0) {
                                    return (
                                        <TableRow
                                            key={user.id}
                                            onClick={() => handleRowClick(user)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: backgroundColor
                                                },
                                                '&:nth-of-type(even)': {
                                                    backgroundColor: backgroundColor
                                                },
                                                '&:hover': {
                                                    backgroundColor: theme.palette.figma.white[30]
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {rankingType === 0 ? user.rankingCumulative : user.rankingMonthly}
                                            </TableCell>
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>{(() => { const l = getLabelByValue(USER_GENDERS, user.gender); return l ? t(l) : '-'; })()}</TableCell>
                                            <TableCell>{user.birth ? formatBirthDate(user.birth) : '-'}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{user.exp?.toLocaleString()}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{user.point?.toLocaleString()}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {todayUsageData[user.id] ? `${todayUsageData[user.id]}p` : '-'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>{user.hasWearable ? t('yes') : t('no')}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>-</TableCell>
                                            <TableCell>-</TableCell>
                                            <TableCell>-</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>-</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>-</TableCell>
                                            <TableCell>{formatDateTimeDisplay(user.createDate, 'date') || '-'}</TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="normal"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditUser(user.id);
                                                    }}
                                                >
                                                    {t('edit')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }

                                // 기관이 있으면 각 기관별로 행 생성
                                return userOrganizations.map((org, orgIndex) => (
                                    <TableRow
                                        key={`${user.id}-${orgIndex}`}
                                        onClick={() => handleRowClick(user)}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: backgroundColor
                                            },
                                            '&:nth-of-type(even)': {
                                                backgroundColor: backgroundColor
                                            },
                                            '&:hover': {
                                                backgroundColor: theme.palette.figma.white[30]
                                            }
                                        }}
                                    >
                                        {/* 첫 번째 기관 행에만 사용자 기본 정보 표시 */}
                                        {orgIndex === 0 ? (
                                            <>
                                                <TableCell sx={{ textAlign: 'right' }}>
                                                    {rankingType === 0 ? user.rankingCumulative : user.rankingMonthly}
                                                </TableCell>
                                                <TableCell>{user.name}</TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>{(() => { const l = getLabelByValue(USER_GENDERS, user.gender); return l ? t(l) : '-'; })()}</TableCell>
                                                <TableCell>{user.birth ? formatBirthDate(user.birth) : '-'}</TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>{user.exp?.toLocaleString()}</TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>{user.point?.toLocaleString()}</TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>
                                                    {todayUsageData[user.id] ? `${todayUsageData[user.id]}p` : '-'}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>{user.hasWearable ? t('yes') : t('no')}</TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>
                                                    {String(user.mainOrganizationId) === String(org.id) ? (
                                                        <StatusIndicator
                                                            status="connect"
                                                            text={t('main_organization')}
                                                        />
                                                    ) : '-'}
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>
                                                    {String(user.mainOrganizationId) === String(org.id) ? (
                                                        <StatusIndicator
                                                            status="connect"
                                                            text={t('main_organization')}
                                                        />
                                                    ) : '-'}
                                                </TableCell>
                                            </>
                                        )}

                                        {/* 기관 정보는 모든 행에 표시 */}
                                        <TableCell>{org.name}</TableCell>
                                        <TableCell>
                                            {org.type && <BoxData
                                                data={(() => { const l = getLabelByValue(ORGANIZATION_TYPES, org.type); return l ? t(l) : '-'; })()}
                                                variant="default"
                                            />}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{org.division || '-'}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{org.section || '-'}</TableCell>
                                        <TableCell>{formatDateTimeDisplay(org.createDate, 'date') || '-'}</TableCell>
                                        <TableCell>
                                            {orgIndex === 0 && (
                                                <Button
                                                    variant="normal"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditUser(user.id);
                                                    }}
                                                >
                                                    {t('edit')}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ));
                            }) : (
                                <TableRow>
                                    <TableCell colSpan={14} sx={{ textAlign: 'center', py: 4 }}>
                                        {loading ? t('loading_data') : t('no_user_data')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 페이징 컨트롤 */}
                {totalCount > 0 && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '20px 0',
                    }}>
                        <Box
                            onClick={handlePrevPage}
                            sx={{
                                width: '20px',
                                height: '20px',
                                cursor: currentPage > 1 ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: currentPage > 1 ? 1 : 0.3,
                            }}
                        >
                            <Prev color={theme.palette.figma.white[80]} size={20} />
                        </Box>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: 500,
                            color: theme.palette.figma.white[80],
                            letterSpacing: '-0.48px',
                            whiteSpace: 'nowrap'
                        }}>
                            {currentPage} / {Math.ceil(totalCount / ITEMS_PER_PAGE)}
                        </Typography>
                        <Box
                            onClick={handleNextPage}
                            sx={{
                                width: '20px',
                                height: '20px',
                                cursor: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 1 : 0.3,
                            }}
                        >
                            <Next color={theme.palette.figma.white[80]} size={20} />
                        </Box>
                    </Box>
                )}
            </Box>

            {/* 하단 사용자추가 버튼 */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, pb: 3, alignSelf: 'stretch' }}>
                <Button
                    onClick={handleAddUser}
                >
                    {t('add_user')}
                </Button>
            </Box>
        </ContentArea>
    );
};

export { UserList };
